import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Phone } from 'lucide-react';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleGoogleCallback, completeGoogleSignup } = useAuth();
  const [error, setError] = useState('');
  const hasProcessed = useRef(false);

  // Correctif S-TEL-GOOGLE : Google ne fournit jamais de numero de telephone.
  // Quand la connexion concerne un email inconnu, le serveur ne cree plus le
  // compte tout de suite : il renvoie needs_phone et un jeton court. On
  // demande le numero ici, exactement comme le formulaire d'inscription
  // classique le fait, avant de creer le compte.
  const [signupToken, setSignupToken] = useState(null);
  const [emailGoogle, setEmailGoogle] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  const redirigerSelonRole = (user) => {
    if (user.role === 'admin') {
      navigate('/admin', { replace: true });
    } else if (user.role === 'doctor') {
      navigate('/doctor/dashboard', { replace: true });
    } else {
      navigate('/patient/dashboard', { replace: true });
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');

      if (!code) {
        setError('Code invalide');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      try {
        const resultat = await handleGoogleCallback(code);

        if (resultat?.needs_phone) {
          setSignupToken(resultat.signup_token);
          setEmailGoogle(resultat.email || '');
          return;
        }

        // Redirection ecrite ici telle quelle (et non via redirigerSelonRole)
        // pour ne pas ajouter de dependance a ce useEffect : la regle
        // react-hooks/exhaustive-deps la signalerait, et le build Vercel
        // traite les avertissements comme des erreurs.
        if (resultat.role === 'admin') {
          navigate('/admin', { replace: true });
        } else if (resultat.role === 'doctor') {
          navigate('/doctor/dashboard', { replace: true });
        } else {
          navigate('/patient/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError("Erreur d'authentification");
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    processCallback();
  }, [location, handleGoogleCallback, navigate]);

  const validerNumero = async (e) => {
    e.preventDefault();
    setPhoneError('');
    setEnvoiEnCours(true);
    try {
      const user = await completeGoogleSignup(signupToken, phone.trim());
      redirigerSelonRole(user);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPhoneError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
            ? detail[0].msg
            : "Numéro invalide. Vérifiez et réessayez."
      );
      setEnvoiEnCours(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">{error}</div>
          <p className="text-[#64748B]">Redirection vers la page de connexion...</p>
        </div>
      </div>
    );
  }

  if (signupToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <form
          onSubmit={validerNumero}
          className="w-full max-w-md bg-white rounded-2xl border border-[#E2E8F0] p-8"
          data-testid="google-phone-form"
        >
          <div className="w-12 h-12 rounded-full bg-[#E0F2FE] flex items-center justify-center mb-5">
            <Phone className="w-6 h-6 text-[#0596DE]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2 font-['Outfit']">
            Encore une chose
          </h1>
          <p className="text-[#64748B] mb-6 text-sm leading-relaxed">
            Indiquez votre numéro de téléphone pour terminer votre inscription.
            Il permet à votre praticien de vous joindre et de vous envoyer le
            rappel de votre rendez-vous.
          </p>

          {emailGoogle && (
            <p className="text-xs text-[#64748B] mb-4">
              Compte Google : <span className="font-medium text-[#0F172A]">{emailGoogle}</span>
            </p>
          )}

          <label htmlFor="phone" className="block text-sm font-medium text-[#0F172A] mb-2">
            Numéro de téléphone
          </label>
          <input
            id="phone"
            type="tel"
            autoFocus
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0555 12 34 56"
            className="w-full rounded-lg border border-[#E2E8F0] px-4 py-3 text-[#0F172A] outline-none focus:border-[#0596DE]"
            data-testid="google-phone-input"
          />

          {phoneError && (
            <div className="mt-3 bg-[#FCEBEB] border border-[#F7C1C1] text-[#A32D2D] rounded-lg px-4 py-2.5 text-sm">
              {phoneError}
            </div>
          )}

          <button
            type="submit"
            disabled={envoiEnCours || !phone.trim()}
            className="mt-6 w-full bg-[#0596DE] text-white rounded-lg py-3 font-medium disabled:opacity-60"
            data-testid="google-phone-submit"
          >
            {envoiEnCours ? 'Création du compte...' : 'Terminer mon inscription'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#0596DE] animate-spin mx-auto mb-4" />
        <p className="text-[#64748B]">Connexion en cours...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
